import { useState } from "react";
import * as Lucide from "lucide-react";
import { ACTION_TYPES, newCustomAction, replacePlaceholders, actionTypeLabel } from "../lib/customActions";
import { Trash2, Plus, ChevronUp, ChevronDown, Eye, EyeOff, Zap } from "lucide-react";

/**
 * Editor reutilizavel para "acoes personalizadas" dentro do ConfigurePanel dos drawers.
 *
 * Props:
 *   value: [{id, label, icon, type, visible, config}]
 *   onChange: (nextList) => void
 *   samplePlaceholders: {chave: valor} - para dar preview do texto substituido.
 */
const COMMON_ICONS = ["Zap", "Send", "Link", "Mail", "Phone", "FileText", "MessageSquare", "Copy", "ExternalLink", "Globe", "AlertTriangle", "Sparkles", "CheckSquare"];

export default function CustomActionsEditor({ value, onChange, samplePlaceholders = {} }) {
  const list = Array.isArray(value) ? value : [];
  const [editingId, setEditingId] = useState(null);

  const update = (id, patch) => onChange(list.map((a) => (a.id === id ? { ...a, ...patch, config: { ...a.config, ...(patch.config || {}) } } : a)));
  const remove = (id) => onChange(list.filter((a) => a.id !== id));
  const add = () => { const n = newCustomAction(); onChange([...list, n]); setEditingId(n.id); };
  const move = (idx, dir) => {
    const to = idx + dir;
    if (to < 0 || to >= list.length) return;
    const next = [...list];
    const [it] = next.splice(idx, 1);
    next.splice(to, 0, it);
    onChange(next);
  };

  const placeholderKeys = Object.keys(samplePlaceholders || {});

  return (
    <div data-testid="custom-actions-editor" className="px-2 py-2 space-y-2">
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Acoes personalizadas</div>
        <button onClick={add} data-testid="custom-action-add"
          className="text-[11px] text-blue hover:underline inline-flex items-center gap-1"><Plus size={11} /> Adicionar</button>
      </div>
      {placeholderKeys.length > 0 && (
        <div className="text-[10px] text-slate-500 bg-slate-50 border border-slate-200 rounded px-2 py-1 leading-tight">
          <b className="text-navy">Placeholders disponiveis:</b> {placeholderKeys.map((k) => `{${k}}`).join(", ")}
        </div>
      )}
      {list.length === 0 && (
        <div className="text-[11px] text-slate-400 italic text-center py-2">Sem acoes personalizadas. Adicione uma para abrir URLs, enviar emails ou chamar webhooks.</div>
      )}
      {list.map((a, idx) => {
        const IconComp = Lucide[a.icon] || Zap;
        const expanded = editingId === a.id;
        return (
          <div key={a.id} data-testid={`custom-action-row-${a.id}`}
            className={`border rounded-md ${expanded ? "border-blue/40 bg-blue/5" : "border-slate-200 bg-white"}`}>
            <div className="flex items-center gap-1.5 px-2 py-1.5">
              <IconComp size={13} className="text-slate-500 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-[12px] font-medium text-navy truncate">{a.label}</div>
                <div className="text-[10px] text-slate-500">{actionTypeLabel(a.type)}</div>
              </div>
              <button onClick={() => update(a.id, { visible: !a.visible })}
                data-testid={`custom-action-toggle-${a.id}`}
                title={a.visible ? "Ocultar" : "Mostrar"}
                className={`p-1 rounded hover:bg-slate-100 ${a.visible ? "text-slate-600" : "text-slate-300"}`}>
                {a.visible ? <Eye size={12} /> : <EyeOff size={12} />}
              </button>
              <button onClick={() => move(idx, -1)} disabled={idx === 0}
                data-testid={`custom-action-up-${a.id}`}
                className="text-slate-400 hover:text-navy disabled:opacity-30 p-0.5"><ChevronUp size={12} /></button>
              <button onClick={() => move(idx, 1)} disabled={idx === list.length - 1}
                data-testid={`custom-action-down-${a.id}`}
                className="text-slate-400 hover:text-navy disabled:opacity-30 p-0.5"><ChevronDown size={12} /></button>
              <button onClick={() => setEditingId(expanded ? null : a.id)}
                data-testid={`custom-action-edit-${a.id}`}
                className="text-[10px] text-blue hover:underline px-1">{expanded ? "fechar" : "editar"}</button>
              <button onClick={() => remove(a.id)} data-testid={`custom-action-remove-${a.id}`}
                className="text-red-500 hover:text-red-700 p-0.5"><Trash2 size={12} /></button>
            </div>
            {expanded && (
              <div className="px-2 py-2 border-t border-slate-200 space-y-1.5">
                <ActionField label="Nome">
                  <input value={a.label} onChange={(e) => update(a.id, { label: e.target.value })}
                    data-testid={`custom-action-label-${a.id}`}
                    className="w-full px-2 py-1 text-[12px] border border-slate-300 rounded" />
                </ActionField>
                <ActionField label="Tipo">
                  <select value={a.type} onChange={(e) => update(a.id, { type: e.target.value })}
                    data-testid={`custom-action-type-${a.id}`}
                    className="w-full px-2 py-1 text-[12px] border border-slate-300 rounded bg-white">
                    {ACTION_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </ActionField>
                <ActionField label="Icone">
                  <select value={a.icon || "Zap"} onChange={(e) => update(a.id, { icon: e.target.value })}
                    data-testid={`custom-action-icon-${a.id}`}
                    className="w-full px-2 py-1 text-[12px] border border-slate-300 rounded bg-white">
                    {COMMON_ICONS.map((n) => <option key={n} value={n}>{n}</option>)}
                  </select>
                </ActionField>

                {a.type === "open_url" && (
                  <ActionField label="URL (usa {chave})">
                    <input value={a.config.url || ""} onChange={(e) => update(a.id, { config: { url: e.target.value } })}
                      data-testid={`custom-action-url-${a.id}`} placeholder="https://portal.exemplo.pt/cliente/{nif}"
                      className="w-full px-2 py-1 text-[12px] border border-slate-300 rounded font-mono" />
                  </ActionField>
                )}

                {a.type === "send_email" && (
                  <>
                    <ActionField label="Destinatario">
                      <input value={a.config.email_to || ""} onChange={(e) => update(a.id, { config: { email_to: e.target.value } })}
                        data-testid={`custom-action-emailto-${a.id}`} placeholder="{email}"
                        className="w-full px-2 py-1 text-[12px] border border-slate-300 rounded font-mono" />
                    </ActionField>
                    <ActionField label="Assunto">
                      <input value={a.config.subject || ""} onChange={(e) => update(a.id, { config: { subject: e.target.value } })}
                        data-testid={`custom-action-subject-${a.id}`} placeholder="Follow-up {nome}"
                        className="w-full px-2 py-1 text-[12px] border border-slate-300 rounded" />
                    </ActionField>
                    <ActionField label="Corpo (texto simples)">
                      <textarea value={a.config.body || ""} onChange={(e) => update(a.id, { config: { body: e.target.value } })}
                        data-testid={`custom-action-body-${a.id}`} rows={3}
                        className="w-full px-2 py-1 text-[12px] border border-slate-300 rounded" />
                    </ActionField>
                  </>
                )}

                {a.type === "copy_text" && (
                  <ActionField label="Texto a copiar">
                    <textarea value={a.config.text || ""} onChange={(e) => update(a.id, { config: { text: e.target.value } })}
                      data-testid={`custom-action-text-${a.id}`} rows={2}
                      placeholder="NIF {nif} - {nome}"
                      className="w-full px-2 py-1 text-[12px] border border-slate-300 rounded font-mono" />
                  </ActionField>
                )}

                {a.type === "webhook" && (
                  <>
                    <ActionField label="URL do webhook">
                      <input value={a.config.url || ""} onChange={(e) => update(a.id, { config: { url: e.target.value } })}
                        data-testid={`custom-action-url-${a.id}`} placeholder="https://n8n.exemplo.pt/webhook/xyz"
                        className="w-full px-2 py-1 text-[12px] border border-slate-300 rounded font-mono" />
                    </ActionField>
                    <ActionField label="Metodo">
                      <select value={a.config.method || "POST"} onChange={(e) => update(a.id, { config: { method: e.target.value } })}
                        data-testid={`custom-action-method-${a.id}`}
                        className="w-full px-2 py-1 text-[12px] border border-slate-300 rounded bg-white">
                        {["POST", "GET", "PUT", "DELETE", "PATCH"].map((m) => <option key={m} value={m}>{m}</option>)}
                      </select>
                    </ActionField>
                    <ActionField label="Payload JSON (usa {chave})">
                      <textarea value={a.config.payload || ""} onChange={(e) => update(a.id, { config: { payload: e.target.value } })}
                        data-testid={`custom-action-payload-${a.id}`} rows={3}
                        placeholder='{"nif":"{nif}","email":"{email}"}'
                        className="w-full px-2 py-1 text-[12px] border border-slate-300 rounded font-mono" />
                    </ActionField>
                    <ActionField label="Headers JSON (opcional)">
                      <textarea value={a.config.headers || ""} onChange={(e) => update(a.id, { config: { headers: e.target.value } })}
                        data-testid={`custom-action-headers-${a.id}`} rows={2}
                        placeholder='{"Authorization":"Bearer ..."}'
                        className="w-full px-2 py-1 text-[12px] border border-slate-300 rounded font-mono" />
                    </ActionField>
                  </>
                )}

                {placeholderKeys.length > 0 && (
                  <Preview action={a} samplePlaceholders={samplePlaceholders} />
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function ActionField({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">{label}</span>
      {children}
    </label>
  );
}

function Preview({ action, samplePlaceholders }) {
  const cfg = action.config || {};
  let previewText = "";
  if (action.type === "open_url" || action.type === "webhook") previewText = replacePlaceholders(cfg.url, samplePlaceholders);
  else if (action.type === "send_email") previewText = `${replacePlaceholders(cfg.email_to || "{email}", samplePlaceholders)} - ${replacePlaceholders(cfg.subject, samplePlaceholders)}`;
  else if (action.type === "copy_text") previewText = replacePlaceholders(cfg.text, samplePlaceholders);
  if (!previewText) return null;
  return (
    <div className="text-[10px] text-slate-500 bg-white border border-slate-200 rounded px-2 py-1" data-testid={`custom-action-preview-${action.id}`}>
      <span className="uppercase tracking-wider font-semibold text-slate-400 mr-1">preview:</span>
      <span className="font-mono break-all">{previewText}</span>
    </div>
  );
}
